import { ESPreset, InputBlocks, PresetFilters } from "@shared/interfaces/PresetBuilder.interface";
import { CheckboxBlock, DateBlock, SelectBlock, TypeBlock } from "@containers/PresetBuilder/components/InputBlock";
import { DEFAULT_BUY_BOX_METRICS } from "@shared/constants/index";
import { PresetError } from "@containers/Preset/interfaces/PresetState.interface";

export enum ESFilterProductType {
  INVESTOR = "INVESTOR",
  REALTOR = "REALTOR",
}

export const PRESET_INPUT_BLOCKS: Record<string, InputBlocks> = {
  select: SelectBlock,
  input: TypeBlock,
  date: DateBlock,
  checkboxes: CheckboxBlock,
};

export const STRING_TYPE = "string";
export const BOOLEAN_TYPE = true;
export const NUMBER_TYPE = 1;

export const DISTRESSORS_BUCKETS = ["Stacked", "Vacant", "Niche", "Blanket"];

export const PRESET_FILTERS_DEFAULT_VALUES: PresetFilters = {
  product_type: ESFilterProductType.INVESTOR,

  expended_criteria: false,
  mailing_list: false,
  dnc: false,

  name: "",
  monthly_records_count: null,

  ai_score_min: 50,
  ai_score_max: 100,
  ai_score_not_defined: false,

  property_type: [],
  property_type_not_defined: true,
  property_type_option: "include",

  property_value_min: null,
  property_value_max: null,
  property_value_not_defined: true,

  equity_percentage_est_min: null,
  equity_percentage_est_max: null,
  equity_percentage_est_not_defined: true,

  bedroom_count_min: null,
  bedroom_count_max: null,
  bedroom_count_not_defined: true,

  bath_count_min: null,
  bath_count_max: null,
  bath_count_not_defined: true,

  property_sqft_min: null,
  property_sqft_max: null,
  property_sqft_not_defined: true,

  year_built_min: null,
  year_built_max: null,
  year_built_not_defined: true,

  area_lot_sf_min: null,
  area_lot_sf_max: null,
  area_lot_sf_not_defined: true,

  last_sale_price_min: null,
  last_sale_price_max: null,
  last_sale_price_not_defined: true,

  last_sale_date_max: null,
  last_sale_date_min: null,
  last_sale_date_not_defined: true,

  year_of_ownership_min: null,
  year_of_ownership_max: null,
  year_of_ownership_not_defined: true,

  distressors_buckets: [],

  // Ownership / Vacancy Status
  owner_occupied: null,

  property_zips_option: "include",
  property_zips: [],

  property_cities_option: "include",
  property_cities: [],

  property_neighborhoods_option: "include",
  property_neighborhoods: [],
};

export const PRESET_RECORD_DEFAULT_VALUES: ESPreset = {
  filters: {
    ...PRESET_FILTERS_DEFAULT_VALUES,
  },
  states: [],
  counties: [],
  zips: [],
  fileName: null,
  processings: [],
  esPreviewRecords: [],
  status: null,
  estimated_records_count: null,
  investor_data_settings_id: null,
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

export const PRESET_DEFAULT_ERRORS = {
  [PresetError.createPresetError]: null,
  [PresetError.updatePresetError]: null,
  [PresetError.deletePresetError]: null,
  [PresetError.getPresetError]: null,
  [PresetError.getPresetListError]: null,
  [PresetError.getPresetFieldsError]: null,
};

export const PRESET_BUILDER_DEFAULT_STATE = {
  filterState: {
    states: [],
    counties: [],
    zips: [],
    property_zips: [],
    property_cities: [],
    property_neighborhoods: [],
  },

  filters: {
    states: [],
    counties: [],
    zips: [],
    cities: [],
    neighborhoods: [],
  },

  presetList: null,

  buyBoxMetrics: {
    ...DEFAULT_BUY_BOX_METRICS,
  },
};

export const MAILING_LIST_FIELDS = [
  "Mailing Address County",
  "Mailing Address",
  "Mailing Address Unit Prefix",
  "Mailing Address Unit",
  "Mailing Address City",
  "Mailing Address State",
  "Mailing Address Zip",
  "Mailing Address Zip4",
];

export const ES_PRESET_FILTER_VALUES: (keyof PresetFilters)[] = [
  "product_type",
  "ai_score_min",
  "ai_score_max",
  "ai_score_not_defined",
  "dnc",
  "mailing_list",
  "name",
  "monthly_records_count",
  "property_type",
  "property_type_not_defined",
  "property_type_option",
  "property_value_min",
  "property_value_max",
  "property_value_not_defined",
  "equity_percentage_est_min",
  "equity_percentage_est_max",
  "equity_percentage_est_not_defined",
  "bedroom_count_min",
  "bedroom_count_max",
  "bedroom_count_not_defined",
  "bath_count_min",
  "bath_count_max",
  "bath_count_not_defined",
  "property_sqft_min",
  "property_sqft_max",
  "property_sqft_not_defined",
  "year_built_min",
  "year_built_max",
  "year_built_not_defined",
  "area_lot_sf_min",
  "area_lot_sf_max",
  "area_lot_sf_not_defined",
  "year_of_ownership_min",
  "year_of_ownership_max",
  "year_of_ownership_not_defined",
  "last_sale_date_min",
  "last_sale_date_max",
  "last_sale_date_not_defined",
  "last_sale_price_min",
  "last_sale_price_max",
  "last_sale_price_not_defined",
  "is_court_order",
  "is_final_judgment",
  "is_lis_pendens",
  "is_notice_of_default",
  "is_notice_of_foreclosure",
  "is_recent_deliquent",
  "is_probate_properties",
  "is_pre_probate",
  "is_divorce_property",
  "is_bankruptcy_property",
  "is_inheritance_property",
  "is_hoa_lien",
  "is_other_lien",
  "is_judgment_liens",
  "is_state_tax",
  "is_federal_lien",
  "is_eviction",
  "is_vacant_property",
  "is_zombie_property",
  "is_short_term_loan",
  "is_absentee_owners",
  "is_oos_owners",
  "is_senior_homeowners",
  "is_low_income",
  "is_bad_credit",
  "is_free_and_clear",
  "is_estate_sales",
  "is_high_equity",
  "is_tired_landlord",
  "is_criminal_felony",
  "owner_occupied",
  "property_zips",
  "property_zips_option",
  "distressors_not_defined",
  "distressors_buckets",
  "property_cities",
  "property_cities_option",
  "property_neighborhoods",
  "property_neighborhoods_option",
];
